import { useEffect, useRef, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `delay` ms have
 * passed without a change. Use to collapse rapid input (e.g. fast pagination
 * clicks) into a single downstream effect/request.
 *
 * The UI can bind to the immediate `value` (stays responsive) while the API call
 * keys off the debounced value (fires once, for the final value).
 *
 * @template T
 * @param {T} value
 * @param {number} [delay=300]
 * @returns {T}
 */
export function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  const first = useRef(true);

  useEffect(() => {
    // Apply the initial value synchronously (no delay on mount).
    if (first.current) { first.current = false; setDebounced(value); return undefined; }
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}
